/**
 * Map Boundaries Plugin for Search Results Map Variant
 *
 * Manages the lifecycle of the market area KML boundary layer and coordinates
 * map positioning using backend-suggested coordinate bounds.
 *
 * Responsibilities:
 * - Listen for 'onNewLocationBoundaries' custom events emitted by the fetch epic
 * - Manage the market area KML layer lifecycle (create, persist, remove)
 * - Position the map using backend-suggested bounds as a baseline
 * - Extend backend bounds with actual KML bounds when the KML loads successfully
 * - Silently fall back to backend bounds when KML fails to load
 * - Clean up layer references and subscriptions on stop
 *
 * Design principles:
 * - All business/orchestration logic lives exclusively in this plugin layer.
 * - All Google Maps interactions go through context.services.mapFacade (the facade).
 * - Boundary data flows via the mapBoundariesUpdated$ Subject (observables.js) — no Redux, no DOM events.
 * - Uses switchMap to cancel stale in-flight KML load operations when the URL changes.
 * - Consecutive identical URLs are deduplicated by distinctUntilChanged upstream.
 *
 * @module searchResultsMap/plugins/mapBoundaries.plugin
 */

import { combineLatest, filter, from, map, Observable, of, startWith, switchMap, tap, EMPTY } from 'rxjs';
import { selectLocationType, LocationType } from 'features/searchResultsFacets';
import { mapBoundariesUpdated$ } from '../redux/observables';

export default function createMapBoundariesPlugin(mapVisible$) {
  const state = {
    started: false,
    mapFacade: null,
    getState: null,
    subscriptions: [],
    mapEventUnsubscribers: [],
    userHasInteracted: false,
    hasHandledFirstLoad: false,
    lastProcessedBoundaries: null,
  };

  function onUserInteraction() {
    state.userHasInteracted = true;
  }

  /**
   * Fits the map to the provided backend bounds.
   * Used both as the baseline and as the silent fallback on KML failure.
   *
   * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }|null} bounds
   */
  function applyBackendBounds(bounds) {
    if (bounds) {
      state.mapFacade.fitBounds(bounds);
    }
  }

  /**
   * Removes the current KML layer and resets the tracked URL.
   */
  function clearCurrentLayer() {
    state.mapFacade.removeKmlLayer();
  }

  /**
   * Loads a new KML layer for the given boundary URL and orchestrates map positioning.
   *
   * Flow:
   * 1. Capture a camera snapshot before the async KML load begins.
   * 2. Create the KML layer via the facade (previous layer is removed inside createKmlLayer)
   * 3. Wait for the KML to settle (success or failure) via onKmlSettled
   * 4. If the camera has moved since step 1 (user panned/zoomed), skip fitBounds entirely.
   * 5. On KML success with valid bounds: fit the map to the KML bounds exclusively.
   * 6. On KML failure: silently fall back to backend bounds only.
   *
   * Using switchMap in the parent subscription ensures that if a new boundary URL arrives
   * before the current KML settles, this observable is cancelled and a new one starts.
   *
   * @returns {import('rxjs').Observable}
   */
  function loadKmlAndPosition(boundaryUrl, backendBounds) {
    const { onKmlSettled } = state.mapFacade.createKmlLayer(boundaryUrl);

    return from(onKmlSettled).pipe(
      tap(({ success, kmlBounds }) => {
        const isFirstLoad = !state.hasHandledFirstLoad;
        state.hasHandledFirstLoad = true;

        if (isFirstLoad && state.userHasInteracted) {
          return;
        }

        if (success && kmlBounds) {
          state.mapFacade.fitBounds(kmlBounds);
        } else {
          applyBackendBounds(backendBounds);
        }
      }),
    );
  }

  /**
   * Sets up the subscription to mapBoundariesUpdated$ that drives the KML lifecycle.
   *
   * - Reacts only when the boundary URL changes (via distinctUntilChanged on the URL).
   * - Uses switchMap to cancel any in-flight KML load when a new URL arrives.
   * - Skips reload when the URL matches the currently loaded layer (persistence).
   * - When mapVisible$ is provided, defers processing until the map is visible and
   *   replays the latest boundaries once visibility is restored.
   */
  function subscribeToMapBoundaries() {
    const boundaries$ = mapBoundariesUpdated$.pipe(
      startWith(null)
    );

    const mapReady$ = mapVisible$.pipe(
      switchMap((isVisible) => {
        if (!isVisible) {
          return of(false);
        }

        return new Observable((observer) => {
          const unsubscribe = state.mapFacade.onceMapEvent('idle', () => {
            observer.next(true);
            observer.complete();
          });
          return unsubscribe;
        });
      })
    );

    const source$ = combineLatest({
      boundaries: boundaries$,
      isVisible: mapReady$,
    }).pipe(
      filter(({ boundaries, isVisible }) => Boolean(isVisible && boundaries)),
      map(({ boundaries }) => boundaries)
    );

    state.subscriptions.push(
      source$.pipe(
        switchMap((mapBoundaries) => {
          if (mapBoundaries === state.lastProcessedBoundaries && state.userHasInteracted) {
            return EMPTY;
          }

          state.lastProcessedBoundaries = mapBoundaries;

          const boundaryUrl = mapBoundaries?.boundaryUrl;
          const backendBounds = mapBoundaries?.bounds;
          clearCurrentLayer();

          if (!boundaryUrl) {
            state.hasHandledFirstLoad = true;
            if (selectLocationType(state.getState()) !== LocationType.RectangularArea.toString()) {
              applyBackendBounds(backendBounds);
            }

            return EMPTY;
          }
          
          return loadKmlAndPosition(boundaryUrl, backendBounds);
        }),
      ).subscribe()
    );
  }

  return {
    name: 'map-boundaries',

    async init(context) {
      context.state.mapBoundaries = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      if (!state.mapFacade) {
        return;
      }

      state.getState = context.services.getState;

      const unsubscribeBoundsChanged = state.mapFacade.onceMapEvent('bounds_changed', onUserInteraction);
      state.mapEventUnsubscribers.push(unsubscribeBoundsChanged);

      subscribeToMapBoundaries();

      state.started = true;
      context.state.mapBoundaries = {
        started: true,
      };
    },

    async stop(context) {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      state.mapEventUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
      state.mapEventUnsubscribers = [];

      state.mapFacade?.removeKmlLayer?.();

      state.started = false;
      state.mapFacade = null;
      state.getState = null;
      state.userHasInteracted = false;
      state.hasHandledFirstLoad = false;
      state.lastProcessedBoundaries = null;

      delete context.state.mapBoundaries;
    },
  };
}
